import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'

import { Bill } from '@features/bills/types/bill'
import { Contact } from '@features/contacts/types/contact'

import { AttachmentParsedData } from '../../../../../types/attachmentParsedData'
import { formatDate } from '../../../../../utils'

enum BillDataTrackingStatus {
  Manual = 'manual',
  Prefilled = 'prefilled',
  Overwritten = 'overwritten',
  NotSet = 'not_set',
}

const getTrackedValueStatus = (
  parsedDataValue?: string | number | null,
  billValue?: string | number | null,
): BillDataTrackingStatus => {
  if (isEmpty(parsedDataValue) && isEmpty(billValue)) {
    return BillDataTrackingStatus.NotSet
  } else if (isEmpty(parsedDataValue) && !isEmpty(billValue)) {
    return BillDataTrackingStatus.Manual
  } else if (isEqual(parsedDataValue, billValue)) {
    return BillDataTrackingStatus.Prefilled
  } else {
    return BillDataTrackingStatus.Overwritten
  }
}

export const getBillDataTrackingStatuses = (
  bill?: Bill & { vendor?: Partial<Contact> },
  attachmentParsedData?: AttachmentParsedData,
) => {
  if (!bill || !attachmentParsedData) {
    return {}
  }

  const prefillingTotalAmountStatus = getTrackedValueStatus(
    attachmentParsedData.amounts.totalInclVat?.toString(),
    bill?.grossAmount?.toString(),
  )

  const prefillingVatStatus = getTrackedValueStatus(
    attachmentParsedData.amounts.totalVat?.toString(),
    bill?.tax?.toString(),
  )

  const prefillingVendorStatus = getTrackedValueStatus(attachmentParsedData.vendor?.name, bill?.vendor?.name)
  const prefillingDateStatus = getTrackedValueStatus(
    formatDate(attachmentParsedData.dates.issued),
    bill?.entryDate?.toString(),
  )
  const prefillingDueDateStatus = getTrackedValueStatus(
    formatDate(attachmentParsedData.dates.due),
    bill?.dueDate?.toString(),
  )
  const prefillingCurrencyStatus = getTrackedValueStatus(attachmentParsedData.currency, bill?.currencyId)
  const allStatusesExceptVatAndDueDate = [prefillingTotalAmountStatus, prefillingDateStatus, prefillingCurrencyStatus]
  const isAnyStatusOverwritten = allStatusesExceptVatAndDueDate.some(
    (status) => status === BillDataTrackingStatus.Overwritten,
  )
  const isAllStatusesPrefilled = allStatusesExceptVatAndDueDate.every(
    (status) => status === BillDataTrackingStatus.Prefilled,
  )

  let prefillingOverallStatus = BillDataTrackingStatus.Manual
  if (isAnyStatusOverwritten) {
    prefillingOverallStatus = BillDataTrackingStatus.Overwritten
  } else if (isAllStatusesPrefilled) {
    prefillingOverallStatus = BillDataTrackingStatus.Prefilled
  }

  return {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    prefilling_overall_status: prefillingOverallStatus,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    prefilling_total_amount_status: prefillingTotalAmountStatus,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    prefilling_vat_status: prefillingVatStatus,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    prefilling_due_date_status: prefillingDueDateStatus,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    prefilling_date_status: prefillingDateStatus,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    prefilling_currency_status: prefillingCurrencyStatus,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    prefilling_vendor_status: prefillingVendorStatus,
  }
}
